// apps/frontend/src/lib/platform.ts
// Détection du contexte d'exécution de Glowp : navigateur web, app native
// iOS ou app native Android (toutes deux via Capacitor).
//
// Pourquoi c'est utile : la règle App Store 3.1.1 d'Apple interdit de vendre
// un abonnement numérique via un autre système que son In-App Purchase. Tant
// que Glowp n'a pas intégré l'IAP, on masque entièrement la VENTE
// d'abonnements dans l'app iOS native. Le web et Android ne sont pas
// concernés et gardent le flux Stripe inchangé.
//
// Important : seule la *vente* est masquée. La gestion d'un abonnement déjà
// actif (voir son plan, l'annuler) reste TOUJOURS autorisée — Apple l'exige.

import { Capacitor } from '@capacitor/core';

/** true si l'app tourne dans un conteneur natif Capacitor (iOS ou Android). */
export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Plateforme courante : 'ios' | 'android' | 'web'. */
export function getPlatform(): 'ios' | 'android' | 'web' {
  try {
    const p = Capacitor.getPlatform();
    return p === 'ios' || p === 'android' ? p : 'web';
  } catch {
    return 'web';
  }
}

/** true uniquement dans l'app iOS native (Capacitor). */
export function isIOSNative(): boolean {
  return isNativeApp() && getPlatform() === 'ios';
}

/**
 * true si l'app a le droit de vendre / promouvoir des abonnements payants.
 *
 * Faux uniquement sur iOS natif, où la règle Apple 3.1.1 l'interdit tant que
 * l'In-App Purchase n'est pas intégré. Web et Android renvoient toujours true.
 *
 * À utiliser pour masquer : page de tarifs, boutons d'upgrade, modales
 * d'upsell, teasers Premium/Pro. Ne JAMAIS l'utiliser pour bloquer la
 * gestion d'un abonnement déjà actif (annulation, consultation du plan).
 */
export function canSellSubscriptions(): boolean {
  return !isIOSNative();
}
